import { TFunction } from 'i18next';
import { ServiceSwitches } from 'src/models/useDashboardServicesModel';
import {
  SERVICE_NAME_MAP,
  SERVICE_NAME_ORDER_MAP,
  SERVICE_TYPES,
} from 'src/utils/service';

import { ServiceNameEnum } from '../types';
import { computePercent, urlMask } from './common';
import { getUtcTime } from './time';

export const CHECKBOX_COLUMN_ID = 'checkbox';

export function formatLastColumn(day: string): string {
  switch (day) {
    case '24h':
      return 'dashboard.chart.column.last24h';
    case '1w':
      return 'dashboard.chart.column.last1w';
    case '1M':
      return 'dashboard.chart.column.last1M';
  }
  return '';
}

export function formatPrevColumn(day: string): string {
  switch (day) {
    case '24h':
      return 'dashboard.chart.column.prev24h';
    case '1w':
      return 'dashboard.chart.column.prev1w';
    case '1M':
      return 'dashboard.chart.column.prev1M';
  }
  return '';
}

export type TableTopX = {
  key?: string;
  total?: number;
  aliases?: string[];
  subject?: string;
  last_detected?: string;
  doc_count?: number;
  details?: { tm_timestamp: string[] };
}[];

export function formatTableTopX(
  inTable: TableTopX,
  keys: string[],
  t: TFunction<'translation'>,
  timeZone?: string
) {
  if (!inTable) {
    return [];
  }

  const outTable = [];
  for (const inRow of inTable) {
    const outRow = [];
    for (const key of keys) {
      if (key === 'key') {
        outRow.push(inRow.key || '');
      } else if (key === 'total') {
        outRow.push(inRow.total || 0);
      } else if (key === 'aliases') {
        outRow.push(inRow.aliases?.[0] || '');
      } else if (key === 'subject') {
        outRow.push(inRow.subject || '');
      } else if (key === 'last_detected') {
        outRow.push(
          getUtcTime(
            inRow.last_detected,
            timeZone,
            t('common.momentDateFormat')
          )
        );
      } else if (key === 'doc_count') {
        outRow.push(inRow.doc_count || 0);
      } else if (key === 'tm_timestamp') {
        outRow.push(
          getUtcTime(
            inRow.details?.tm_timestamp?.[0],
            timeZone,
            t('common.momentDateFormat')
          )
        );
      }
    }
    outTable.push(outRow);
  }
  return outTable;
}

export type TableContentType = {
  key?: string;
  total?: number;
  last_detected?: string;
}[];

export function formatTableContentType(
  data: TableContentType,
  keys: string[],
  t: TFunction<'translation'>,
  timeZone?: string
) {
  if (!data) {
    return [];
  }
  const table = [];
  for (const item of data) {
    const row: (string | number)[] = [];
    for (const key of keys) {
      if (key === 'key') {
        row.push(t(`common.contentType.${item.key}`));
      } else if (key === 'total') {
        row.push(item.total || 0);
      } else if (key === 'last_detected') {
        row.push(
          getUtcTime(
            item.last_detected,
            timeZone,
            t('common.momentDateFormat')
          ) || ''
        );
      }
    }
    table.push(row);
  }
  return table;
}

export function formatTime(timestamp?: string, timeZone?: string) {
  if (timestamp) {
    if (timeZone) {
      return getUtcTime(timestamp, timeZone, 'DD MMM YYYY');
    } else {
      return timestamp;
    }
  } else {
    return '';
  }
}

export interface TableServices {
  [service: string]: { total?: number };
}

export function formatTableServices(
  lastTable: TableServices,
  prevTable: TableServices,
  services: ServiceSwitches
) {
  if (!lastTable || !prevTable) {
    return [];
  }

  const lastSummary = formatServicesImpl(lastTable);
  const prevSummary = formatServicesImpl(prevTable);

  return SERVICE_TYPES.filter(({ name }) => services[name]).map(
    ({ name }): [ServiceNameEnum, number, number, number] => {
      const last = lastSummary[name] || 0;
      const prev = prevSummary[name] || 0;
      const percent = computePercent(last, prev);
      return [name, last, prev, percent];
    }
  );
}

function formatServicesImpl(inTable: TableServices) {
  const outTable: { [key: string]: number } = {};
  for (const [key, value] of Object.entries(inTable)) {
    //key is service name in es
    const svcName = SERVICE_NAME_MAP.get(key);
    if (svcName) {
      outTable[svcName] = value.total ?? 0;
    }
  }
  return outTable;
}

export interface TableCounts {
  [name: string]: number;
}

export function formatTableCounts(
  lastInTable: TableCounts,
  prevInTable: TableCounts
) {
  if (!lastInTable || !prevInTable) {
    return [];
  }

  const names: string[] = [];
  for (const name of Object.keys(lastInTable)) {
    if (names.indexOf(name) === -1) {
      names.push(name);
    }
  }
  for (const name of Object.keys(prevInTable)) {
    if (names.indexOf(name) === -1) {
      names.push(name);
    }
  }

  const outTable: [string, number, number, number][] = [];
  for (const name of names) {
    const last = lastInTable[name] || 0;
    const prev = prevInTable[name] || 0;
    const percent = computePercent(last, prev);
    outTable.push([name, last, prev, percent]);
  }
  return outTable;
}

export function formatTable(
  data: any,
  type: 'URL' | 'FILE',
  day: string,
  riskMap: Map<string, string>,
  offset: string,
  timeFormat: string
) {
  const inTable = data?.virtual_analyzerTopSuspiciousObjects?.[type]?.[day];
  if (!inTable) {
    return [];
  }
  return inTable.map((val: any) => {
    let maskUrl = '';
    if (Array.isArray(val._source?.tm_block_reason)) {
      maskUrl = val._source?.tm_block_reason.join(' ; ');
      if (type === 'URL') {
        maskUrl = urlMask(maskUrl);
      }
    } else {
      maskUrl = val._source?.tm_block_reason
        ? val._source?.tm_block_reason
        : '';
      console.warn(`tm_block_reason is not a array`);
    }

    return [
      maskUrl,
      val.doc_count || 0,
      riskMap.get(val._source?.tm_DDAaas_risk_level) ||
        val._source?.tm_DDAaas_risk_level,
      val._source?.tm_file_name || '',
      getUtcTime(val._source?.tm_timestamp || '', offset, timeFormat),
      val.key,
    ];
  });
}

export function replaceTableData(
  data: any,
  replaceMap: Map<string, string>,
  replaceIndex: number,
  t: TFunction<'translation'>
) {
  data.forEach((item: any[]) => {
    const valueMapped = replaceMap.get(item[replaceIndex]);
    if (valueMapped === undefined) {
      console.error(
        `Unexpected value: ${item[replaceIndex]}, can't be replaced`
      );
    } else {
      item[replaceIndex] = t(valueMapped);
    }
  });
}

//sort table data related to services
export function sortTableDataByService(
  data: [ServiceNameEnum, number, number, number][]
) {
  data.sort((d1, d2) => {
    return SERVICE_NAME_ORDER_MAP[d1[0]] - SERVICE_NAME_ORDER_MAP[d2[0]];
  });
  return data;
}
